import React from "react";

/**
 * CommunitySection — soft, no-purchase CTA that captures signups exclusively into
 * the MailerLite "AI Growth - Faculty Community" group.
 *
 * Rendering strategy:
 *   Render the MailerLite share-URL iframe from the very first paint. No JS
 *   embed timeout, no state flip, no MutationObserver — the previous approaches
 *   left a visibly-empty `.ml-embedded` div on the page for 2.5 s and, when
 *   universal.js couldn't hydrate the form for this account, sometimes never
 *   swapped in the iframe at all. The share URL is the single source of truth
 *   for the live form; embedding it directly is deterministic on every route,
 *   every refresh, every user agent.
 */
const ML_ACCOUNT_ID = "2414544";
const ML_FORM_ID = "198151217719805924";
const ML_IFRAME_SRC = `https://preview.mailerlite.io/forms/${ML_ACCOUNT_ID}/${ML_FORM_ID}/share`;

export default function CommunitySection({ variant = "band" }) {
    const isBand = variant === "band";

    return (
        <section
            data-testid="community-section"
            className={
                isBand
                    ? "border-t border-black/10 bg-white"
                    : "border-t border-b border-black/10 bg-secondary"
            }
        >
            <div className="container-edge py-16 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <p className="overline" data-testid="community-eyebrow">COMMUNITY</p>
                        <h2
                            data-testid="community-headline"
                            className="mt-3 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]"
                        >
                            Join the faculty AI community
                        </h2>
                        <p
                            data-testid="community-subhead"
                            className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed"
                        >
                            Practical tips for teaching with AI — no hype. Free weekly notes from Dr. Genevieve Dobson.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div
                            className="border border-black/10 bg-white p-6 sm:p-8"
                            data-testid="community-embed-wrapper"
                        >
                            <iframe
                                src={ML_IFRAME_SRC}
                                title="Join the faculty AI community"
                                data-testid="community-iframe-fallback"
                                className="w-full block"
                                style={{ minHeight: 480, border: "none" }}
                                loading="lazy"
                            />
                            <p
                                data-testid="community-microcopy"
                                className="mt-4 text-xs text-muted-foreground"
                            >
                                Unsubscribe anytime. We never share your email.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
